import { useState } from "react";
import { Alert, Button, Stack } from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
} from "@bw/shared/api";
import type {
  StripeFrontendConfigResponse,
  UnifiedEnrollmentResponse,
} from "@bw/shared/types/generated";
import {
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
} from "../../../common/components/payment";
import { useResumeEnrollmentPayment } from "../hooks/useMyEnrollments";

type Authentication = {
  clientSecret: string;
  config: StripeFrontendConfigResponse;
};

function originalAuthentication(error: unknown): Authentication | null {
  const authentication = getPaymentAuthenticationRequired(error);
  if (!authentication || !isApiError(error)) return null;
  const publishableKey = error.details?.publishable_key;
  const account = error.details?.connected_account_id;
  const mode = error.details?.mode;
  if (
    typeof publishableKey !== "string" ||
    !publishableKey ||
    typeof account !== "string" ||
    !account ||
    (mode !== "test" && mode !== "live")
  )
    return null;
  return {
    clientSecret: authentication.clientSecret,
    config: {
      publishable_key: publishableKey,
      connected_account_id: account,
      mode,
    },
  };
}

/** Current pending inventory resumes only the original Program-owned operation. */
export function PendingPaymentRecovery({
  enrollment,
  canAct,
}: {
  enrollment: UnifiedEnrollmentResponse;
  canAct: () => boolean;
}) {
  const resume = useResumeEnrollmentPayment();
  const [authentication, setAuthentication] = useState<Authentication | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  async function runResume() {
    if (!canAct() || resume.isPending) return;
    setError(null);
    try {
      await resume.mutateAsync(enrollment.enrollment_id);
      setAuthentication(null);
    } catch (failure) {
      const challenge = originalAuthentication(failure);
      if (challenge) setAuthentication(challenge);
      else
        setError(
          isApiError(failure)
            ? failure.message
            : "Payment recovery unavailable. Try the same request again.",
        );
    }
  }

  async function finishAuthentication(clientSecret: string) {
    if (
      !authentication ||
      authentication.clientSecret !== clientSecret ||
      resume.isPending
    )
      throw new Error(
        "Original payment recovery unavailable. Retry the same request.",
      );
    try {
      await resume.mutateAsync(enrollment.enrollment_id);
      setAuthentication(null);
      setError(null);
    } catch (failure) {
      const replacement = originalAuthentication(failure);
      if (replacement) setAuthentication(replacement);
      else if (isTerminalPaymentAttemptError(failure)) setAuthentication(null);
      throw failure;
    }
  }

  return (
    <Stack spacing={1.5} sx={{ alignItems: "flex-start" }}>
      {error ? <Alert severity="warning">{error}</Alert> : null}
      {authentication ? (
        <MaterialConnectedElementsProvider config={authentication.config}>
          <MaterialPaymentIntentAuthentication
            clientSecret={authentication.clientSecret}
            onAuthenticated={finishAuthentication}
          />
        </MaterialConnectedElementsProvider>
      ) : (
        <Button
          size="small"
          variant="outlined"
          disabled={resume.isPending || !canAct()}
          onClick={() => void runResume()}
        >
          {resume.isPending ? "Resuming payment…" : "Resume payment"}
        </Button>
      )}
    </Stack>
  );
}
